/******************************************************************************
 *** BgtOvrlImplExpndList ** 총괄조합비 수입/지출 완료 조회 * 지출결의서 리스트
*******************************************************************************/
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import GridTable, { fldInfsProps } from "@/js/common/ui/GridTable";
import Modal from "@/js/common/ui/modal/Modal";
import AdminGridStyle from "@/js/common/ui/style/AdminGridStyle";
import Util from "@/js/common/Util";
import React from "react";
import DialogTitleBar from "../../common/DialogTitleBar";
import BgtOvrlExpndViewDialog from "./dialog/BgtOvrlExpndViewDialog";

interface Props {
    ognzCd    : string;  // 조직코드(지부)
    acntgYear : string;  // 회계연도
    searchKywd: string;  // 검색어(적요)
}
const BgtOvrlImplExpndList = ( {ognzCd, acntgYear, searchKywd}: Props ) => {
    const [expenseDataArr, setExpenseDataArr ] = React.useState<any[]>([]);  // 수입결의서 목록
    const [sumExpenseAmt , setSumExpenseAmt  ] = React.useState<string>(""); // 수입 내역 합계

    const [ovrlExpndModalOpen , setOvrlExpndModalOpen ] = React.useState<boolean>(false); // 수입결의서 상세정보 모달
    const [ovrlMbsdsExpndDocId, setOvrlMbsdsExpndDocId] = React.useState<string>(""); // 지출결의서 문서 ID

    // GridTable 컬럼 정의
    const expenseFldInfs: fldInfsProps[] = [
        { type: "date" , fldNm: "implDt"             , width: 120, headAlign: "center", dataAlign: "center", label: "행위년월일" },
        { type: "text" , fldNm: "grnamtSeNm"         , width:  70, headAlign: "center", dataAlign: "center", label: "구분"     },
        { type: "text" , fldNm: "bgtSbjct1stNm"      , width: 100, headAlign: "center", dataAlign: "center", label: "관"       },
        { type: "text" , fldNm: "bgtSbjct2stNm"      , width: 100, headAlign: "center", dataAlign: "center", label: "항"       },
        { type: "text" , fldNm: "bgtSbjct3stNm"      , width: 100, headAlign: "center", dataAlign: "center", label: "목"       },
        { type: "text" , fldNm: "abstrCn"            , width: 180, headAlign: "center", dataAlign: "left"  , label: "적요"     },
        { type: "comma", fldNm: "implAmt"            , width: 120, headAlign: "center", dataAlign: "right", label: "금액"      },
        { type: "text" , fldNm: "atrzSttsNm"         , width:  80, headAlign: "center", dataAlign: "center", label: "진행상태" },
        { type: "text" , fldNm: "crtDt"              , width: 120, headAlign: "center", dataAlign: "center", label: "작성일시" },
        { type: "text" , fldNm: "expndRsdocNo"       , width: 140, headAlign: "center", dataAlign: "center", label: "문서번호" },
    ];

    // 총괄조합비 지출결의서 목록 조회
    React.useEffect(() => {
        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/ovrl/cmptn/select-ovrl-cmptn-expnd-list.do", // 지출결의서 완료 조회
            methodType: "post",
            paramType: "json",
            paramData: {
                orgCd     : ognzCd    , // 조직코드
                acntgYear : acntgYear , // 회계연도
            },
            onSuccessFn: (res: any) => {
                setExpenseDataArr(res.result.list);
                setSumExpenseAmt(res.result.listSum.sumImplAmt);
                AppContext.hideSpinner();
            },
        });
    }, [ognzCd, acntgYear]);

    React.useEffect(() => {
        let sum = 0;
        for (const row of Util.filterFieldData(expenseDataArr, expenseFldInfs, "abstrCn", searchKywd)) {
            sum += Number(row.implAmt) || 0;
        }
        setSumExpenseAmt(Util.comma(String(sum)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expenseDataArr, searchKywd]);

    return (
        <div className="bd1d9d9d9 bdrd6 bgcWhite p15">
            <div className="dpFlx aiC jcSb mb10 h44">
                <span className="fm-b fs16 fc007bff ls1 fw700">지출 결의서</span>
                <div className="mt20 dpFlx">
                    <span className="fm-Pretendard-Light fs16 ls1 fc222222 pr3">합계</span>
                    <span className="fm-Pretendard-Medium fs16 ls1 fc007bff ml10">{Util.comma(sumExpenseAmt)}</span>
                    <span className="fm-Pretendard-Light fs13 ls1 fc222222 pl2 pr3 mt3">원</span>
                </div>
            </div>

            {/* 그리드 */}
            <GridTable
                tableInfo={{
                    tableHeadVsb: true,
                    styleType: AdminGridStyle,
                    fldInfs: expenseFldInfs,
                    ordFld: { name: "implDt", direction: "asc" },
                    dataArr: Util.filterFieldData(expenseDataArr, expenseFldInfs, "abstrCn", searchKywd),
                    maxRowNums: [30, 50, 100],
                    theadTrHeight: 40,
                    tbodyTrHeight: 35,
                    paginateClass: "pt10",
                    paginateStyle: {
                        buttonOnColor: AppContext.boardThemeColor,
                        buttonWidth: 22,
                        buttonHeight: 24,
                        buttonMarginLeft: 1,
                        buttonMarginRight: 1,
                        fontSize: 13,
                        color: "#666666",
                        letterSpacing: -0.8,
                        fontFamily: "Pretendard-Regular",
                        borderRadius: 3,
                    },
                    footerVsb: true,
                    rowClickFunc: (item: any) => {  // row click
                        setOvrlMbsdsExpndDocId(item.ovrlMbsdsExpndDocId);
                        setOvrlExpndModalOpen(true);
                    },
                }}
            />

            {/* 지출결의서 상세정보 모달 */}
            <Modal modalOpen={ovrlExpndModalOpen} dim={true} visible={true}
                titleEl={<DialogTitleBar title="지출결의서 상세내용" />}
                onCloseFunc={() => {
                    setOvrlMbsdsExpndDocId("");
                    setOvrlExpndModalOpen(false);
                }}
            >
                <div className="h800">
                    <BgtOvrlExpndViewDialog
                        ovrlMbsdsExpndDocId   = {ovrlMbsdsExpndDocId}     // 지출결의서 문서 ID
                        setOvrlExpndModalOpen = {setOvrlExpndModalOpen}
                    />
                </div>
            </Modal>
        </div>
    )
}

export default BgtOvrlImplExpndList;